/**
 * CV upload constraints — ported from af/auth/lib/validations/file-upload.ts
 *
 * Single source of truth for the accepted CV formats and max size. Both the
 * `accept` attribute of the file inputs and the runtime validation derive from
 * these constants, so the two can never drift apart.
 *
 * Validation is done on the file extension (like af/auth) and not on
 * `file.type`: browsers report inconsistent MIME types for text files, which
 * previously made `.txt` uploads fail (QA-12130).
 */

export const ACCEPTED_CV_FORMATS = ['pdf', 'txt'] as const;

/**
 * MIME types matching ACCEPTED_CV_FORMATS. Used as a fallback when a file has
 * no name — notably `ALFileUpload` emits a nameless `Blob` (it re-wraps the
 * picked `File`), so extension-based validation can't apply there. The DS
 * already gates emitted files to these same MIME types, so this stays in sync.
 */
export const ACCEPTED_CV_MIME_TYPES = ['application/pdf', 'text/plain'] as const;

/** Maximum file size in MB */
export const MAX_FILE_SIZE_MB = 2;

/** Maximum file size in bytes */
export const MAX_FILE_SIZE = MAX_FILE_SIZE_MB * 1024 * 1024;

/** Value for the `accept` attribute of a file input — e.g. ".pdf,.txt" */
export const CV_ACCEPT_ATTRIBUTE = ACCEPTED_CV_FORMATS.map(format => `.${format}`).join(',');

/** Human readable format list — e.g. "PDF, TXT" */
export const CV_FORMATS_LABEL = ACCEPTED_CV_FORMATS.map(format => format.toUpperCase()).join(', ');

/** Get file extension in lower case */
export function getFileExtension(filename: string): string {
  return filename.split('.').pop()?.toLowerCase() ?? '';
}

/** MIME type → canonical extension, for the accepted CV formats. */
const MIME_TO_EXTENSION: Record<string, string> = {
  'application/pdf': 'pdf',
  'text/plain': 'txt'
};

/**
 * Filename to send to the backend when uploading a CV.
 *
 * `ALFileUpload` emits a nameless `Blob`, so `file.name` is usually empty and
 * the file would reach Viktor as "blob" (no extension) — which its format
 * validation rejects. We synthesize `cv.<ext>` from the MIME type so Viktor
 * receives a valid extension, mirroring af/auth which forwards a real filename.
 */
export function cvUploadFilename(file: File | Blob): string {
  const name = (file as File).name;
  if (name) return name;
  return `cv.${MIME_TO_EXTENSION[file.type] ?? 'pdf'}`;
}

/**
 * Validate file format against the accepted CV formats.
 *
 * Prefers extension-based validation (see QA-12130 — MIME is unreliable for
 * `.txt`). When the file has no name — e.g. the nameless `Blob` emitted by
 * `ALFileUpload` — it falls back to the file's MIME type, which the design
 * system has already constrained to ACCEPTED_CV_MIME_TYPES before emitting.
 */
export function validateCvFormat(file: File | Blob): boolean {
  const name = (file as File).name;
  if (name) {
    const extension = getFileExtension(name);
    return ACCEPTED_CV_FORMATS.includes(extension as (typeof ACCEPTED_CV_FORMATS)[number]);
  }
  return ACCEPTED_CV_MIME_TYPES.includes(file.type as (typeof ACCEPTED_CV_MIME_TYPES)[number]);
}

/** Validate file size against the max allowed size */
export function validateCvSize(file: File): boolean {
  return file.size <= MAX_FILE_SIZE;
}
